import React, { useContext, useMemo } from 'react';

import {
  type QueryParams as IdeaActivityQueryParams,
  invalidateIdeaActivityQueries,
  loadIdeaActivity,
  loadMoreIdeaActivity,
  reloadIdeaActivityQuery,
  removeIdeaActivities,
  upsertIdeaComments,
} from 'common/actions/ideaActivityQueries';
import {
  type QueryParams,
  loadIdea,
  recomputeAllIdeaScores,
  reloadIdea,
} from 'common/actions/ideas';
import connect from 'common/core/connect';

import type { IdeaActivity, IdeaComment } from 'common/api/endpoints/ideaActivity';
import type { QueryState } from 'common/reducers/queryReducerFactory';

type IdeasContextValue = {
  loadIdea: (queryParams: QueryParams) => Promise<void>;
  loadIdeaActivity: (queryParams: IdeaActivityQueryParams) => Promise<void>;
  loadMoreIdeaActivity: (query: QueryState<IdeaActivity, IdeaActivityQueryParams>) => Promise<void>;
  invalidateIdeaActivityQueries: () => Promise<void>;
  reloadIdea: ({ ideaID }: { ideaID: string }) => Promise<void>;
  reloadIdeaActivityQuery: (queryParams: IdeaActivityQueryParams) => Promise<void>;
  recomputeAllIdeaScores: () => Promise<void>;
  removeIdeaActivities: (queryParams: IdeaActivityQueryParams, activityIDs: string[]) => void;
  upsertIdeaComments: (queryParams: IdeaActivityQueryParams, comments: IdeaComment[]) => void;
};

export const IdeasContext = React.createContext<IdeasContextValue>({
  loadIdea: (_queryParams: QueryParams) => Promise.resolve(),
  loadIdeaActivity: (_queryParams: IdeaActivityQueryParams) => Promise.resolve(),
  loadMoreIdeaActivity: (_query: QueryState<IdeaActivity, IdeaActivityQueryParams>) =>
    Promise.resolve(),
  invalidateIdeaActivityQueries: () => Promise.resolve(),
  reloadIdea: ({ ideaID: _ideaID }: { ideaID: string }) => Promise.resolve(),
  reloadIdeaActivityQuery: (_queryParams: IdeaActivityQueryParams) => Promise.resolve(),
  recomputeAllIdeaScores: () => Promise.resolve(),
  removeIdeaActivities: (_queryParams: IdeaActivityQueryParams, _activityIDs: string[]) => {},
  upsertIdeaComments: (_queryParams: IdeaActivityQueryParams, _comments: IdeaComment[]) => {},
});

export const useIdeasContext = () => {
  const context = useContext(IdeasContext);
  if (!context) {
    throw new Error('useIdeasContext must be used within a IdeasContainer');
  }
  return context;
};

type ConnectProps = {
  loadIdea: (queryParams: QueryParams) => Promise<void>;
  loadIdeaActivity: (queryParams: IdeaActivityQueryParams) => Promise<void>;
  loadMoreIdeaActivity: (query: QueryState<IdeaActivity, IdeaActivityQueryParams>) => Promise<void>;
  invalidateIdeaActivityQueries: () => Promise<void>;
  reloadIdea: ({ ideaID }: { ideaID: string }) => Promise<void>;
  reloadIdeaActivityQuery: (queryParams: IdeaActivityQueryParams) => Promise<void>;
  recomputeAllIdeaScores: () => Promise<void>;
  removeIdeaActivities: (queryParams: IdeaActivityQueryParams, activityIDs: string[]) => void;
  upsertIdeaComments: (queryParams: IdeaActivityQueryParams, comments: IdeaComment[]) => void;
};
type OwnProps = {
  children: React.ReactNode;
};
type Props = ConnectProps & OwnProps;
function IdeasContainer({
  children,
  loadIdea,
  loadIdeaActivity,
  loadMoreIdeaActivity,
  invalidateIdeaActivityQueries,
  reloadIdea,
  reloadIdeaActivityQuery,
  recomputeAllIdeaScores,
  removeIdeaActivities,
  upsertIdeaComments,
}: Props) {
  const contextValue = useMemo(
    () => ({
      loadIdea,
      loadIdeaActivity,
      loadMoreIdeaActivity,
      invalidateIdeaActivityQueries,
      reloadIdea,
      recomputeAllIdeaScores,
      reloadIdeaActivityQuery,
      removeIdeaActivities,
      upsertIdeaComments,
    }),
    [
      loadIdea,
      loadIdeaActivity,
      loadMoreIdeaActivity,
      invalidateIdeaActivityQueries,
      reloadIdea,
      reloadIdeaActivityQuery,
      recomputeAllIdeaScores,
      removeIdeaActivities,
      upsertIdeaComments,
    ],
  );
  return <IdeasContext.Provider value={contextValue}>{children}</IdeasContext.Provider>;
}

export default connect(null, {
  loadIdea,
  loadIdeaActivity,
  loadMoreIdeaActivity,
  invalidateIdeaActivityQueries,
  reloadIdea,
  reloadIdeaActivityQuery,
  recomputeAllIdeaScores,
  removeIdeaActivities,
  upsertIdeaComments,
})(IdeasContainer) as unknown as React.FC<OwnProps>;
